"use client";

import { useState, useEffect, useCallback, useRef } from "react";

type Photo = { id: string; url: string; altText?: string | null };

export default function PhotoGallery({ photos, title }: { photos: Photo[]; title: string }) {
  const [openAt, setOpenAt] = useState<number | null>(null);
  // Mobile-only: index of the photo currently visible in the snap carousel,
  // used to render the "3 / 12" counter overlay so visitors know there's
  // more to swipe through.
  const [carouselIdx, setCarouselIdx] = useState(0);
  const carouselRef = useRef<HTMLDivElement | null>(null);

  const close = useCallback(() => setOpenAt(null), []);
  const next = useCallback(() => {
    setOpenAt((i) => (i == null ? null : (i + 1) % photos.length));
  }, [photos.length]);
  const prev = useCallback(() => {
    setOpenAt((i) => (i == null ? null : (i - 1 + photos.length) % photos.length));
  }, [photos.length]);

  useEffect(() => {
    if (openAt == null) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") close();
      else if (e.key === "ArrowRight") next();
      else if (e.key === "ArrowLeft") prev();
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [openAt, close, next, prev]);

  // Update carousel counter as the user swipes. IntersectionObserver picks
  // the most-visible slide; debounced via the observer's threshold so we
  // don't flicker between adjacent photos mid-swipe.
  useEffect(() => {
    const root = carouselRef.current;
    if (!root) return;
    const slides = Array.from(root.querySelectorAll<HTMLElement>("[data-r2l-slide]"));
    if (slides.length === 0) return;
    const io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting && entry.intersectionRatio > 0.5) {
            const idx = Number(entry.target.getAttribute("data-r2l-slide"));
            if (!Number.isNaN(idx)) setCarouselIdx(idx);
          }
        }
      },
      { root, threshold: 0.6 },
    );
    slides.forEach((s) => io.observe(s));
    return () => io.disconnect();
  }, [photos.length]);

  if (photos.length === 0) {
    return (
      <div className="aspect-[16/9] bg-neutral-100 rounded-xl flex items-center justify-center text-neutral-400 text-sm">
        no photos yet
      </div>
    );
  }

  const hero = photos[0];
  const thumbs = photos.slice(1, 5);

  return (
    <>
      {/* MOBILE: horizontal-snap carousel of every photo. Full-bleed (extends
          to the page edges via -mx-4) so the photo fills the visible width
          without the page's padding eating into it. */}
      <div className="md:hidden -mx-4 relative">
        <div
          ref={carouselRef}
          className="flex overflow-x-auto snap-x snap-mandatory scrollbar-hide"
        >
          {photos.map((p, i) => (
            <div
              key={p.id}
              data-r2l-slide={i}
              className="snap-center shrink-0 w-screen aspect-[4/3] bg-neutral-100"
            >
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img
                src={p.url}
                alt={p.altText || title}
                onClick={() => setOpenAt(i)}
                className="w-full h-full object-cover"
              />
            </div>
          ))}
        </div>
        {/* Counter overlay so visitors know how many photos there are. */}
        {photos.length > 1 ? (
          <div className="absolute bottom-3 right-4 bg-black/60 text-white text-xs font-medium rounded-full px-2.5 py-1">
            {carouselIdx + 1} / {photos.length}
          </div>
        ) : null}
      </div>

      {/* DESKTOP: classic 4-thumb grid with one large hero. */}
      <div className="hidden md:grid relative grid-cols-4 grid-rows-2 gap-2 rounded-xl overflow-hidden aspect-[2/1]">
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img
          src={hero.url}
          alt={hero.altText || title}
          onClick={() => setOpenAt(0)}
          className="col-span-2 row-span-2 h-full w-full object-cover cursor-pointer hover:brightness-95 transition"
        />
        {thumbs.map((p, i) => (
          // eslint-disable-next-line @next/next/no-img-element
          <img
            key={p.id}
            src={p.url}
            alt={p.altText || title}
            onClick={() => setOpenAt(i + 1)}
            className="aspect-square w-full object-cover cursor-pointer hover:brightness-95 transition"
          />
        ))}
        {photos.length > 5 ? (
          <button
            type="button"
            onClick={() => setOpenAt(0)}
            className="absolute bottom-4 right-4 bg-white border border-neutral-300 rounded-lg px-3 py-1.5 text-xs font-medium hover:bg-neutral-50"
          >
            Show all {photos.length} photos
          </button>
        ) : null}
      </div>

      {openAt != null ? (
        <div
          className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center"
          onClick={close}
        >
          {/* Close — bumped from text-3xl bare to a 44px tap target with
              a subtle backdrop so it's clearly reachable on mobile. */}
          <button
            onClick={(e) => { e.stopPropagation(); close(); }}
            aria-label="Close"
            className="absolute top-3 right-3 inline-flex items-center justify-center w-11 h-11 rounded-full bg-black/50 text-white text-2xl hover:bg-black/70"
          >
            ×
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); prev(); }}
            aria-label="Previous"
            className="absolute left-2 inline-flex items-center justify-center w-11 h-11 rounded-full bg-black/50 text-white text-2xl hover:bg-black/70"
          >
            ‹
          </button>
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            src={photos[openAt].url}
            alt={photos[openAt].altText || title}
            onClick={(e) => e.stopPropagation()}
            className="max-h-[88vh] max-w-[92vw] object-contain"
          />
          <button
            onClick={(e) => { e.stopPropagation(); next(); }}
            aria-label="Next"
            className="absolute right-2 inline-flex items-center justify-center w-11 h-11 rounded-full bg-black/50 text-white text-2xl hover:bg-black/70"
          >
            ›
          </button>
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-white text-sm bg-black/50 rounded-full px-3 py-1">
            {openAt + 1} / {photos.length}
          </div>
        </div>
      ) : null}
    </>
  );
}
